import { useState } from "react";
import { Copy, Download, Send, Smartphone, GalleryHorizontal } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { fullPost, isTouch } from "@/lib/share";
import { track } from "@/lib/analytics";

const copyText = (t) => navigator.clipboard?.writeText(t).catch(() => {});

export async function downloadCard(kind, key, format = "square", n = 0) {
  const res = await fetch(`${API}/social/card/${kind}/${key}.jpg?v=2&format=${format}${format === "slide" ? `&n=${n}` : ""}`);
  if (!res.ok) throw new Error("card");
  const blob = await res.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `gkdeals-${kind}-${key}${format === "story" ? "-story" : format === "slide" ? `-slide-${n + 1}` : ""}`.slice(0, 76) + ".jpg";
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 4000);
  return blob;
}

export default function PlatformPost({ p, caption, link, cardUrl, kind, keyId }) {
  const text = fullPost(caption, link);
  const btn = "inline-flex items-center justify-center gap-1.5 h-10 rounded-lg text-sm font-semibold transition-colors";

  const onCopy = () => { copyText(text); track("share", { target: `publisher:copy:${p.id} ${kind}/${keyId}` }); toast.success(`${p.label} caption copied`); };
  const onCard = async () => {
    try { await downloadCard(kind, keyId); toast.success("Card downloaded"); } catch (_) { toast.error("Couldn't create the card"); }
  };
  const onStory = async () => {
    try { await downloadCard(kind, keyId, "story"); toast.success("9:16 story downloaded"); } catch (_) { toast.error("Couldn't create the story"); }
  };
  const onPost = async () => {
    track("share", { target: `publisher:post:${p.id} ${kind}/${keyId}` });
    copyText(text);
    if (p.needsCard && isTouch()) {
      try {
        const res = await fetch(`${API}/social/card/${kind}/${keyId}.jpg`);
        const file = new File([await res.blob()], "gkdeals.jpg", { type: "image/jpeg" });
        if (navigator.canShare?.({ files: [file] })) { await navigator.share({ files: [file], text }); return; }
      } catch (_) { /* fall through to desktop flow */ }
    }
    const w = window.open(p.post(caption, link, cardUrl), "_blank", "noopener,noreferrer");
    if (p.needsCard) { onCard(); toast.success(`Caption copied & card downloaded — paste into ${p.label}`); }
    else if (p.pasteHint) toast.success(`Caption copied! In ${p.label}, tap the post box and choose Paste.`, { duration: 8000 });
    else toast.success("Caption copied — paste it if the post box is empty");
    return w;
  };

  return (
    <div className={`rounded-xl border overflow-hidden ${p.tint}`} data-testid={`publisher-${p.id}`}>
      <div className="flex items-center gap-2.5 px-4 py-3">
        <span className="w-7 h-7 rounded-lg flex items-center justify-center text-white" style={{ background: p.color }}><p.Icon className="w-3.5 h-3.5" /></span>
        <span className="font-semibold text-ink-950">{p.label}</span>
      </div>
      <div className="bg-white px-4 py-4 border-t border-inherit">
        <p className="text-sm text-ink-900 whitespace-pre-line leading-relaxed" data-testid={`publisher-${p.id}-caption`}>{caption.caption}</p>
        <p className="text-sm text-neutral-500 break-all mt-3">{link}</p>
        {caption.hashtags?.length > 0 && <p className="text-xs text-blue-600 mt-3 leading-relaxed">{caption.hashtags.join(" ")}</p>}
        {p.pasteHint && (
          <p className="text-xs text-neutral-500 mt-3 bg-neutral-50 rounded-lg px-3 py-2" data-testid={`publisher-${p.id}-hint`}>
            {p.label} doesn't let websites fill in your post. Your caption is copied for you, so just tap the post box and choose <b>Paste</b>. The product picture and link preview appear once you post.
          </p>
        )}
        <div className="grid grid-cols-[1fr_auto_auto] gap-2 mt-4">
          <button type="button" onClick={onCopy} data-testid={`publisher-${p.id}-copy`} className={`${btn} border border-neutral-200 bg-white hover:bg-neutral-50 text-ink-900`}>
            <Copy className="w-4 h-4 shrink-0" /> Copy<span className="hidden sm:inline"> Caption</span>
          </button>
          <button type="button" onClick={onCard} data-testid={`publisher-${p.id}-card`} className={`${btn} px-3 border border-neutral-200 bg-white hover:bg-neutral-50 text-ink-900`}>
            <Download className="w-4 h-4" /> Card
          </button>
          <button type="button" onClick={onPost} data-testid={`publisher-${p.id}-post`} className={`${btn} px-4 text-white hover:opacity-90`} style={{ background: p.color }}>
            <Send className="w-4 h-4 shrink-0" /> <span className="whitespace-nowrap">Copy & Post</span>
          </button>
        </div>
        {p.id === "instagram" && kind === "city" && <CarouselStrip kind={kind} keyId={keyId} />}
        {p.story && (
          <div className="mt-2 flex items-center gap-2" data-testid={`publisher-${p.id}-story-row`}>
            <a href={`${API}/social/card/${kind}/${keyId}.jpg?format=story&v=2`} target="_blank" rel="noopener noreferrer" data-testid={`publisher-${p.id}-story-preview`}
              className="shrink-0 w-9 h-16 rounded-md overflow-hidden border border-neutral-200 bg-neutral-100">
              <img src={`${API}/social/card/${kind}/${keyId}.jpg?format=story&v=2`} alt="Story preview" loading="lazy" className="w-full h-full object-cover" />
            </a>
            <button type="button" onClick={onStory} data-testid={`publisher-${p.id}-story`}
              className={`${btn} flex-1 px-3 border border-dashed border-neutral-300 bg-white hover:bg-neutral-50 text-ink-900`}>
              <Smartphone className="w-4 h-4" /> Story 9:16
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// 4-slide Instagram carousel for city shares: cover + one slide per top hotel
function CarouselStrip({ kind, keyId }) {
  const [busy, setBusy] = useState(false);
  const slides = [0, 1, 2, 3];
  const src = (n) => `${API}/social/card/${kind}/${keyId}.jpg?format=slide&n=${n}`;
  const all = async () => {
    setBusy(true);
    try {
      for (const n of slides) { await downloadCard(kind, keyId, "slide", n); await new Promise((r) => setTimeout(r, 350)); }
      track("share", { target: `publisher:carousel ${kind}/${keyId}` });
      toast.success("4 carousel slides downloaded — add them to one Instagram post in order");
    } catch (_) { toast.error("Couldn't create the carousel"); }
    setBusy(false);
  };
  return (
    <div className="mt-3 rounded-lg border border-neutral-200 p-2.5" data-testid="publisher-instagram-carousel">
      <p className="text-xs font-semibold text-ink-900 mb-2 flex items-center gap-1.5"><GalleryHorizontal className="w-3.5 h-3.5" /> Carousel post · 4 slides</p>
      <div className="flex gap-1.5">
        {slides.map((n) => (
          <a key={n} href={src(n)} target="_blank" rel="noopener noreferrer" data-testid={`publisher-carousel-slide-${n}`}
            className="relative flex-1 aspect-square rounded-md overflow-hidden border border-neutral-200 bg-neutral-100">
            <img src={src(n)} alt={`Slide ${n + 1}`} loading="lazy" className="w-full h-full object-cover" />
          </a>
        ))}
      </div>
      <button type="button" onClick={all} disabled={busy} data-testid="publisher-carousel-download"
        className="mt-2 w-full inline-flex items-center justify-center gap-1.5 h-10 rounded-lg text-sm font-semibold border border-dashed border-neutral-300 bg-white hover:bg-neutral-50 text-ink-900 disabled:opacity-60">
        <Download className={`w-4 h-4 ${busy ? "animate-bounce" : ""}`} /> {busy ? "Preparing slides…" : "Download all 4"}
      </button>
    </div>
  );
}
